import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import HierarchicalCheckbox from './HierarchicalCheckbox';
import { fetchHHIndustries, type IndustryOption } from '../../services/hhDictionariesService';

interface HierarchicalItem {
  id: string;
  label: string;
  count?: number;
  children?: HierarchicalItem[];
}

interface IndustryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIndustries: string[];
  onSelectionChange: (industries: string[], filterByExpIndustry?: boolean, experiencePeriod?: string) => void;
  filterByExpIndustry?: boolean;
  experiencePeriod?: string;
}

const IndustryModal: React.FC<IndustryModalProps> = ({
  isOpen,
  onClose,
  selectedIndustries,
  onSelectionChange,
  filterByExpIndustry: filterByExpIndustryProp = false,
  experiencePeriod: experiencePeriodProp = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localSelectedIndustries, setLocalSelectedIndustries] = useState<string[]>([]);
  const [localFilterByExpIndustry, setLocalFilterByExpIndustry] = useState(false);
  const [localExperiencePeriod, setLocalExperiencePeriod] = useState('');
  const [isExpPeriodOpen, setIsExpPeriodOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedIndustries(selectedIndustries);
      setLocalFilterByExpIndustry(filterByExpIndustryProp);
      setLocalExperiencePeriod(experiencePeriodProp);
    }
  }, [isOpen, selectedIndustries, filterByExpIndustryProp, experiencePeriodProp]);

  useEffect(() => {
    const loadIndustries = async () => {
      if (!isOpen) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchHHIndustries();
        setIndustries(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Не удалось получить список отраслей');
        setIndustries([]);
      } finally {
        setLoading(false);
      }
    };

    void loadIndustries();
  }, [isOpen]);

  // Преобразование данных из API в формат для HierarchicalCheckbox
  const convertToHierarchicalItems = (items: IndustryOption[]): HierarchicalItem[] => {
    return items.map(item => ({
      id: item.id,
      label: item.name,
      children: item.industries ? convertToHierarchicalItems(item.industries) : undefined
    }));
  };

  const industryData = convertToHierarchicalItems(industries);

  const getAllChildrenIds = (item: HierarchicalItem): string[] => {
    const result: string[] = [];
    if (item.children) {
      for (const child of item.children) {
        result.push(child.id);
        result.push(...getAllChildrenIds(child));
      }
    }
    return result;
  };

  const getAllLeafIds = (item: HierarchicalItem): string[] => {
    if (!item.children || item.children.length === 0) return [item.id];
    let result: string[] = [];
    for (const child of item.children) {
      result = result.concat(getAllLeafIds(child));
    }
    return result;
  };

  // Поиск элемента по ID в дереве
  const findItemById = (items: HierarchicalItem[], id: string): HierarchicalItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const buildParentIndex = (items: HierarchicalItem[], parent: string | null, map: Record<string, string | null>) => {
    for (const it of items) {
      map[it.id] = parent;
      if (it.children && it.children.length > 0) buildParentIndex(it.children, it.id, map);
    }
  };
  const parentIndex: Record<string, string | null> = {};
  buildParentIndex(industryData, null, parentIndex);

  const getAncestors = (id: string): string[] => {
    const result: string[] = [];
    let current: string | null = parentIndex[id] ?? null;
    while (current) {
      result.push(current);
      current = parentIndex[current] ?? null;
    }
    return result;
  };

  const expandForDisplay = (selected: string[]): string[] => {
    const set = new Set<string>();
    for (const sel of selected) {
      set.add(sel);
      const node = findItemById(industryData, sel);
      if (node) {
        for (const cid of getAllChildrenIds(node)) set.add(cid);
      }
    }
    return Array.from(set);
  };

  const compressSelection = (selected: Set<string>): string[] => {
    const walk = (node: HierarchicalItem): { ids: string[]; covered: boolean } => {
      if (selected.has(node.id)) return { ids: [node.id], covered: true };
      if (!node.children || node.children.length === 0) return { ids: [], covered: selected.has(node.id) };
      let allCovered = true;
      let collected: string[] = [];
      for (const child of node.children) {
        const res = walk(child);
        if (!res.covered) allCovered = false;
        collected = collected.concat(res.ids);
      }
      if (collected.length === 0) return { ids: [], covered: false };
      if (allCovered) return { ids: [node.id], covered: true };
      return { ids: collected, covered: false };
    };
    const result: string[] = [];
    for (const root of industryData) {
      const res = walk(root);
      result.push(...res.ids);
    }
    return Array.from(new Set(result));
  };


  const isIndeterminate = (item: HierarchicalItem): boolean => {
    if (!item.children || item.children.length === 0) return false;
    
    const childrenIds = getAllChildrenIds(item);
    const selectedChildren = childrenIds.filter(childId => localSelectedIndustries.includes(childId));
    
    return selectedChildren.length > 0 && selectedChildren.length < childrenIds.length;
  };

  const handleToggle = (id: string, checked: boolean) => {
    const item = findItemById(industryData, id);
    if (!item) return;
    const set = new Set<string>(localSelectedIndustries);
    if (checked) {
      set.add(id);
    } else {
      set.delete(id);
      const ancestors = getAncestors(id);
      const ancestorWithSelection = ancestors.find(aid => set.has(aid));
      if (ancestorWithSelection) {
        set.delete(ancestorWithSelection);
        const ancestorItem = findItemById(industryData, ancestorWithSelection);
        if (ancestorItem) {
          const allLeafs = getAllLeafIds(ancestorItem);
          const removedLeafs = new Set<string>(getAllLeafIds(item));
          for (const leaf of allLeafs) {
            if (!removedLeafs.has(leaf)) set.add(leaf);
          }
        }
      } else if (item.children && item.children.length > 0) {
        for (const cid of getAllChildrenIds(item)) set.delete(cid);
      }
    }
    const normalized = compressSelection(set);
    setLocalSelectedIndustries(normalized);
  };

  const handleToggleExpanded = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleReset = () => {
    setLocalSelectedIndustries([]);
    setLocalFilterByExpIndustry(false);
    setLocalExperiencePeriod('');
  };

  const handleSave = () => {
    onSelectionChange(localSelectedIndustries, localFilterByExpIndustry, localExperiencePeriod);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedIndustries(selectedIndustries);
    setLocalFilterByExpIndustry(filterByExpIndustryProp);
    setLocalExperiencePeriod(experiencePeriodProp);
    onClose();
  };

  const expPeriodOptions = [
    { value: '', label: 'Любой' },
    { value: 'all_time', label: 'За всё время опыта' },
    { value: 'last_year', label: 'За последний год' },
    { value: 'last_three_years', label: 'За последние 3 года' },
    { value: 'last_six_years', label: 'За последние 6 лет' }
  ];

  // Фильтрация данных по поисковому запросу
  const filterItems = (items: HierarchicalItem[], searchTerm: string): HierarchicalItem[] => {
    if (!searchTerm) return items;
    
    return items.filter(item => {
      const matchesSearch = item.label.toLowerCase().includes(searchTerm.toLowerCase());
      const hasMatchingChildren = item.children ? filterItems(item.children, searchTerm).length > 0 : false;
      return matchesSearch || hasMatchingChildren;
    }).map(item => ({
      ...item,
      children: item.children ? filterItems(item.children, searchTerm) : undefined
    }));
  };

  const filteredData = filterItems(industryData, searchTerm);
  const checkedForDisplay = expandForDisplay(localSelectedIndustries);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" data-testid="modal-overlay">
      <div className="modal-container flex flex-col" data-testid="modal-container" data-modal="industry">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-2xl font-bold text-gray-900">Отрасли</h2>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск отраслей"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349]"
            />
          </div>
        </div>

        {/* Период опыта в отрасли */}
        <div className={`px-6 pt-3 pb-3 ${localSelectedIndustries.length === 0 ? 'opacity-50' : ''}`} title={localSelectedIndustries.length === 0 ? 'Сначала выберите отрасль' : ''}>
          <label className="block text-sm font-medium text-gray-900 mb-1">Период опыта в отрасли</label>
          <div className="relative" ref={(node) => {
            const handler = (e: MouseEvent) => {
              if (node && !node.contains(e.target as Node)) setIsExpPeriodOpen(false);
            };
            if (node) {
              document.addEventListener('mousedown', handler);
              (node as any).__cleanup = () => document.removeEventListener('mousedown', handler);
            }
          }}>
            <button
              type="button"
              disabled={localSelectedIndustries.length === 0}
              onClick={() => setIsExpPeriodOpen(!isExpPeriodOpen)}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
            >
              <span className={localExperiencePeriod ? 'text-gray-900' : 'text-gray-500'}>
                {expPeriodOptions.find(o => o.value === localExperiencePeriod)?.label || 'Любой'}
              </span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpPeriodOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 20 20">
                <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="currentColor"/>
              </svg>
            </button>
            {isExpPeriodOpen && (
              <div className="absolute left-0 right-0 bottom-full mb-1 bg-white rounded-lg border border-gray-200 shadow-lg z-10 py-1">
                {expPeriodOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { setLocalExperiencePeriod(opt.value); setIsExpPeriodOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      localExperiencePeriod === opt.value ? 'text-[#e16349] bg-[rgba(225,99,73,0.05)]' : 'text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Искать по последним местам работы */}
        <div className="px-6 pb-3" title={localSelectedIndustries.length === 0 ? 'Сначала выберите отрасль' : ''}>
          <label className={`flex items-center gap-3 ${localSelectedIndustries.length > 0 ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
            <input
              type="checkbox"
              checked={localFilterByExpIndustry}
              disabled={localSelectedIndustries.length === 0}
              onChange={(e) => setLocalFilterByExpIndustry(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#e16349] focus:ring-[#e16349]"
            />
            <span className="text-sm font-medium text-gray-900">Искать по последним местам работы</span>
          </label>
          <p className="text-xs text-gray-500 ml-7 mt-1">Минимум 1 из последних 3 мест работы должно быть в выбранной отрасли</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500">
              Загрузка отраслей...
            </div>
          ) : error ? (
            <div className="flex flex-col items-start gap-3 text-sm text-red-600">
              <span>Не удалось загрузить отрасли: {error}</span>
              <button
                className="text-[#e16349] hover:underline"
                onClick={() => {
                  setError(null);
                  setIndustries([]);
                  setLoading(true);
                  void fetchHHIndustries()
                    .then((data: IndustryOption[]) => {
                      setIndustries(data);
                    })
                    .catch((err: unknown) => {
                      setError(err instanceof Error ? err.message : 'Не удалось получить список отраслей');
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
              >
                Повторить попытку
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500">
              Ничего не найдено
            </div>
          ) : (
            <div className="space-y-2">
              {filteredData.map((item) => (
                <HierarchicalCheckbox
                  key={item.id}
                  item={item}
                  checkedItems={checkedForDisplay}
                  onToggle={handleToggle}
                  expandedItems={expandedItems}
                  onToggleExpanded={handleToggleExpanded}
                  level={0}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4">
          <div className="flex justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Сбросить
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Отменить
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-[#e16349] text-white rounded-lg hover:bg-[#d14d3a] transition-colors"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IndustryModal;